import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ClassDefaults } from '@/types/models';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useOrganizationCoreSettings } from '@/hooks/useOrganizationCoreSettings';
import { useUpdateOrganizationSettings } from '@/hooks/useUpdateOrganizationSettings';

interface ClassDefaultsFormProps {
  organizationId: string; 
  classDefaults: ClassDefaults;
  setClassDefaults: React.Dispatch<React.SetStateAction<ClassDefaults>>;
}

const ClassDefaultsForm: React.FC<ClassDefaultsFormProps> = ({
  organizationId,
  classDefaults,
  setClassDefaults
}) => {
  const [formState, setFormState] = useState<ClassDefaults>(classDefaults);
  const [orgWaitlistsEnabled, setOrgWaitlistsEnabled] = useState<boolean>(true);
  
  // Add organization settings management
  const { organization, setOrganization, loading, error } = useOrganizationCoreSettings(organizationId);
  const updateOrganizationSettings = useUpdateOrganizationSettings(organization, setOrganization, organizationId);
  
  const [parentCanSelfEnrollTrial, setParentCanSelfEnrollTrial] = useState<boolean>(
    organization?.parent_can_self_enroll_trial ?? true
  );
  const [enableWaitlists, setEnableWaitlists] = useState<boolean>(
    organization?.enable_waitlists ?? true
  );

  // Check if organization has waitlists enabled
  useEffect(() => {
    const fetchOrgWaitlistSetting = async () => {
      if (!organizationId) return;
      
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('enable_waitlists')
          .eq('id', organizationId)
          .single();
          
        if (error) throw error;
        setOrgWaitlistsEnabled(data.enable_waitlists ?? true);
      } catch (error) {
        console.error('Error fetching organization waitlist setting:', error);
      }
    };
    
    fetchOrgWaitlistSetting();
  }, [organizationId]);

  // Sync toggle state with organization changes
  useEffect(() => {
    setParentCanSelfEnrollTrial(organization?.parent_can_self_enroll_trial ?? true);
    setEnableWaitlists(organization?.enable_waitlists ?? true);
    setOrgWaitlistsEnabled(organization?.enable_waitlists ?? true);
  }, [organization?.parent_can_self_enroll_trial, organization?.enable_waitlists]);

  const handleChange = (key: keyof ClassDefaults, value: any) => {
    setFormState(prev => ({ ...prev, [key]: value }));
  };

  const handleToggleChange = async (field: string, value: boolean) => {
    try {
      if (field === 'parent_can_self_enroll_trial') {
        setParentCanSelfEnrollTrial(value);
      } else if (field === 'enable_waitlists') {
        setEnableWaitlists(value);
      }

      await updateOrganizationSettings({
        [field]: value,
      });

      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
      
      // Revert the state on error
      if (field === 'parent_can_self_enroll_trial') {
        setParentCanSelfEnrollTrial(!value);
      } else if (field === 'enable_waitlists') {
        setEnableWaitlists(!value);
      }
    }
  };

  const handleSave = async () => {
    try {
      // Update class defaults in the database
      const { error } = await supabase
        .from('organizations')
        .update({ class_defaults: formState })
        .eq('id', organizationId);
      
      if (error) throw error;
      
      // Update local state
      setClassDefaults(formState);
      
      toast.success('Class defaults updated');
    } catch (error) {
      console.error('Error updating class defaults:', error);
      toast.error('Failed to update class defaults');
    }
  };

  return (
    <div className="space-y-6">
      {/* Class Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Class Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="parent-self-enroll">Allow Parents to self-enroll in free trials</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, parents can sign up for free trial classes directly from your website
              </p>
            </div>
            <Switch
              id="parent-self-enroll"
              checked={parentCanSelfEnrollTrial}
              onCheckedChange={(checked) => handleToggleChange('parent_can_self_enroll_trial', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-waitlists">Enable automatic waitlists</Label>
              <p className="text-sm text-muted-foreground">
                Automatically create waitlists when classes reach capacity
              </p>
            </div>
            <Switch
              id="enable-waitlists"
              checked={enableWaitlists}
              onCheckedChange={(checked) => handleToggleChange('enable_waitlists', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Class Defaults Card */}
      <Card>
        <CardHeader>
          <CardTitle>Class Defaults</CardTitle>
        </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="default-duration">
            Default Class Duration (minutes)
          </label>
          <Input
            id="default-duration"
            type="number"
            value={formState.duration_minutes || 60}
            onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value))}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="max-students">
            Default Maximum Students
          </label>
          <Input
            id="max-students"
            type="number"
            value={formState.max_students || 12}
            onChange={(e) => handleChange('max_students', parseInt(e.target.value))}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Default Price Format</label>
          <Select
            value={formState.price_display || 'show_full'}
            onValueChange={(value: 'show_full' | 'show_monthly' | 'show_both' | 'hide') => 
              handleChange('price_display', value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select price format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="show_full">Show Full Price</SelectItem>
              <SelectItem value="show_monthly">Show Monthly Only</SelectItem>
              <SelectItem value="show_both">Show Both</SelectItem>
              <SelectItem value="hide">Hide Price</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {enableWaitlists && (
          <div className="space-y-2">
            <Label htmlFor="waitlist-enabled" className="flex items-center gap-2">
              <span>Enable waitlist by default for new classes</span>
              <Switch
                id="waitlist-enabled"
                checked={formState.waitlist_enabled ?? true}
                onCheckedChange={(checked) => handleChange('waitlist_enabled', checked)}
              />
            </Label>
            <div className="text-xs text-muted-foreground ml-8">
              When enabled, new classes with capacity limits will automatically have waitlists enabled. Can be overridden per class.
            </div>
          </div>
        )}

        {!enableWaitlists && (
          <div className="rounded-lg border p-4 bg-muted/30">
            <p className="text-sm text-muted-foreground">
              Waitlists are disabled for this organization. Enable them in the Class Settings above to use this feature.
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button onClick={handleSave}>Save Defaults</Button>
      </CardFooter>
    </Card>
    </div>
  );
};

export default ClassDefaultsForm;
